import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/auth';

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [formData, setFormData] = useState({
    student_id: '',
    amount: '',
    status: 'pending',
    method: 'cash',
  });

  useEffect(() => {
    fetchPayments();
    fetchStudents();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/payments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/students', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = Array.isArray(response.data)
        ? response.data
        : (response.data.data || []);
      setStudents(list);
    } catch (error) {
      console.error('Erreur élèves:', error);
    }
  };

  const summary = useMemo(() => {
    const total = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const paid = payments
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const pending = payments
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    const now = new Date();
    const thisMonth = payments
      .filter(p => {
        if (p.status !== 'paid' || !p.payment_date) return false;
        const d = new Date(p.payment_date);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    return { total, paid, pending, thisMonth };
  }, [payments]);

  const filteredPayments = useMemo(() => {
    return payments.filter((p) => {
      // Recherche
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const studentName = (p.student?.user?.name || '').toLowerCase();
        const ref = (p.reference || '').toLowerCase();
        if (!studentName.includes(term) && !ref.includes(term)) return false;
      }
      // Filtre statut
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      // Filtre méthode
      if (filterMethod !== 'all' && p.method !== filterMethod) return false;
      return true;
    });
  }, [payments, searchTerm, filterStatus, filterMethod]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingPayment) {
        await api.put(`/payments/${editingPayment.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await api.post('/payments', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingPayment(null);
      setFormData({ student_id: '', amount: '', status: 'pending', method: 'cash' });
      fetchPayments();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce paiement ?')) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/payments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPayments();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      student_id: payment.student_id || '',
      amount: payment.amount || '',
      status: payment.status || 'pending',
      method: payment.method || 'cash',
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingPayment(null);
    setFormData({ student_id: '', amount: '', status: 'pending', method: 'cash' });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'refunded': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'paid': return '✅ Payé';
      case 'pending': return '⏳ En attente';
      case 'failed': return '❌ Échoué';
      case 'refunded': return '🔄 Remboursé';
      default: return status;
    }
  };

  const getMethodLabel = (method) => {
    switch(method) {
      case 'wave': return '🌊 Wave';
      case 'orange_money': return '🟠 Orange Money';
      case 'card': return '💳 Carte';
      case 'cash': return '💵 Espèces';
      case 'bank_transfer': return '🏦 Virement';
      default: return method;
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('fr-FR');
    } catch {
      return date;
    }
  };

  const selectedStudent = students.find(s => s.id === parseInt(formData.student_id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">💳 Gestion des paiements</h1>
          <p className="text-gray-500">Consultez et gérez tous les paiements</p>
        </div>
        <button
          onClick={handleNew}
          className="bg-[#E67E22] text-white px-4 py-2 rounded-lg hover:bg-[#D35400] transition font-medium"
        >
          ➕ Nouveau paiement
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-blue-500">
          <p className="text-xs text-gray-500 mb-1">💰 Total</p>
          <p className="text-xl font-bold text-[#2C3E50]">{formatAmount(summary.total)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-green-500">
          <p className="text-xs text-gray-500 mb-1">✅ Payé</p>
          <p className="text-xl font-bold text-green-600">{formatAmount(summary.paid)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-yellow-500">
          <p className="text-xs text-gray-500 mb-1">⏳ En attente</p>
          <p className="text-xl font-bold text-yellow-600">{formatAmount(summary.pending)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 border-l-4 border-orange-500">
          <p className="text-xs text-gray-500 mb-1">📅 Ce mois</p>
          <p className="text-xl font-bold text-orange-600">{formatAmount(summary.thisMonth)}</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="🔍 Rechercher (élève ou référence)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Tous les statuts</option>
            <option value="paid">✅ Payé</option>
            <option value="pending">⏳ En attente</option>
            <option value="failed">❌ Échoué</option>
            <option value="refunded">🔄 Remboursé</option>
          </select>
          <select
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          >
            <option value="all">Toutes les méthodes</option>
            <option value="cash">💵 Espèces</option>
            <option value="wave">🌊 Wave</option>
            <option value="orange_money">🟠 Orange Money</option>
            <option value="card">💳 Carte</option>
            <option value="bank_transfer">🏦 Virement</option>
          </select>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {filteredPayments.length} paiement(s) affiché(s) sur {payments.length}
        </p>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Référence</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Élève</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Montant</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Méthode</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Statut</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">
                      📭 Aucun paiement trouvé
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map((payment) => (
                    <tr key={payment.id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4 text-xs font-mono text-gray-600">
                        {payment.reference || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {payment.student?.user?.name || `Élève #${payment.student_id}`}
                      </td>
                      <td className="py-3 px-4 font-bold text-[#2C3E50]">
                        {formatAmount(payment.amount)}
                      </td>
                      <td className="py-3 px-4 text-sm">{getMethodLabel(payment.method)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                          {getStatusLabel(payment.status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(payment.payment_date)}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleEdit(payment)}
                          className="text-blue-600 hover:text-blue-800 mr-3 text-lg"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="text-red-600 hover:text-red-800 text-lg"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-[#2C3E50] mb-4">
              {editingPayment ? '✏️ Modifier le paiement' : '➕ Nouveau paiement'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Élève *
                </label>
                <select
                  value={formData.student_id}
                  onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                >
                  <option value="">Sélectionner un élève</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.user?.name || s.name || `Élève #${s.id}`}
                      {s.class?.name ? ` — ${s.class.name}` : ''}
                    </option>
                  ))}
                </select>
                {selectedStudent && (
                  <p className="text-xs text-gray-500 mt-1">
                    Matricule : {selectedStudent.matricule || '-'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Montant (FCFA) *
                </label>
                <input
                  type="number"
                  placeholder="Ex: 50000"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                  required
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Méthode
                </label>
                <select
                  value={formData.method}
                  onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                >
                  <option value="cash">💵 Espèces</option>
                  <option value="wave">🌊 Wave</option>
                  <option value="orange_money">🟠 Orange Money</option>
                  <option value="card">💳 Carte bancaire</option>
                  <option value="bank_transfer">🏦 Virement bancaire</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Statut
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                >
                  <option value="pending">⏳ En attente</option>
                  <option value="paid">✅ Payé</option>
                  <option value="failed">❌ Échoué</option>
                  <option value="refunded">🔄 Remboursé</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingPayment(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] transition font-medium"
                >
                  {editingPayment ? 'Modifier' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayments;